import {
  useLearnerProgress,
  useLearningProgressLearnerDetail,
} from "@/api/services/main/learning-progress";
import { InnerBox } from "@/components/elements/InnerBox";
import { CurriculumProgressBlock } from "@/features/learning-progress/components/learner-detail/curriculum-progress/CurriculumProgressBlock";
import { ProgressDonutChart } from "@/features/learning-progress/components/learner-detail/ProgressDonutChart";
import { divider, black, text, primary } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { CircularProgress, Stack, Grid, Typography, styled } from "@mui/material";
import { FC } from "react";

type TCurrentProgressSectionProps = {
  learnerId: number | string;
};

const StatCard = styled(Stack)`
  padding: 8px;
  background-color: #ecf9f9;
  border-radius: ${rounded.xs};
  width: 100px;
`;

const InfoItem: FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Stack gap={1} sx={{ pb: 2, borderBottom: `1px solid ${divider.middle}` }}>
    <Typography variant="cap12" color={black[70]}>
      {label}
    </Typography>
    <Typography variant="body14Semi" color={text.primary}>
      {value || "-"}
    </Typography>
  </Stack>
);

export const CurrentProgressSection: FC<TCurrentProgressSectionProps> = ({ learnerId }) => {
  const { data: progressRes, isLoading } = useLearnerProgress({ learnerId });
  const { data: learnerDetailRes } = useLearningProgressLearnerDetail({ learnerId });
  const data = progressRes?.data;
  const encouragementMessage = learnerDetailRes?.data.notes?.encouragementMsg ?? "";

  if (isLoading || !data) {
    return (
      <InnerBox p={3} showDivider={false} borderColor={divider.high} bordered title="現在の進行情報">
        <Stack alignItems="center" py={6}>
          <CircularProgress size={24} />
        </Stack>
      </InnerBox>
    );
  }

  const actual = data.weeklyActualMinutes ?? 0;
  const target = data.weeklyTargetMinutes ?? 0;
  const progressPercent = target > 0 ? Math.round((actual / target) * 100) : 0;
  // weeklyProgressText format: "{actual}/{target}分" e.g. "384/480分"
  const [progressActualText = "", progressRestText = ""] = (data.weeklyProgressText ?? "").split(/\/(.+)/);

  return (
    <InnerBox p={3} showDivider={false} borderColor={divider.high} bordered title="現在の進行情報">
      {/* Top stats row */}
      <Stack gap={2} mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <InfoItem label="総学習時間" value={data.totalStudyTime} />
          </Grid>
          <Grid item xs={6}>
            <InfoItem label="今週までの総目標時間" value={data.targetTimeUntilThisWeek} />
          </Grid>
        </Grid>

        {/* Last progressed subject - full width */}
        <InfoItem label="最後に進めた科目" value={data.lastSubjectName} />
      </Stack>

      {/* Donut chart + stats */}
      <Stack direction="row" mt={3} gap={4} alignItems="flex-start" justifyContent="center">
        {/* Left: Chart area */}
        <Stack alignItems="center" gap={0.5} sx={{ minWidth: 168 }}>
          <Typography variant="body14Semi" color={black[70]}>
            今週の学習時間
          </Typography>
          <ProgressDonutChart percent={progressPercent} />
          <Stack direction="row" gap={0.5} alignItems="baseline">
            <Typography variant="body14" color={black[70]}>
              目標:
            </Typography>
            <Typography variant="body14Bold" color={primary[400]}>
              {progressActualText}
            </Typography>
            <Typography variant="body14" color={black[70]}>
              /{progressRestText}
            </Typography>
          </Stack>
        </Stack>

        {/* Right: Stat cards */}
        <Stack gap={0.5}>
          <StatCard>
            <Typography variant="cap12" color={black[70]} sx={{ fontWeight: 500 }}>
              今日の学習時間
            </Typography>
            <Stack direction="row" alignItems="baseline" justifyContent="center" gap={0.5}>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: primary[400] }}>
                {data.todayStudyMinutes ?? 0}
              </Typography>
              <Typography variant="body14Semi" color={black[70]}>
                分
              </Typography>
            </Stack>
          </StatCard>
          <StatCard>
            <Typography variant="cap12" color={black[70]} sx={{ fontWeight: 500 }}>
              連続学習日数
            </Typography>
            <Stack direction="row" alignItems="baseline" justifyContent="center" gap={0.5}>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: primary[400] }}>
                {data.consecutiveStudyDays ?? 0}
              </Typography>
              <Typography variant="body14Semi" color={black[70]}>
                日
              </Typography>
            </Stack>
          </StatCard>
          <StatCard>
            <Typography variant="cap12" color={black[70]} sx={{ fontWeight: 500 }}>
              総学習日数
            </Typography>
            <Stack direction="row" alignItems="baseline" justifyContent="center" gap={0.5}>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: primary[400] }}>
                {data.totalStudyDays ?? 0}
              </Typography>
              <Typography variant="body14Semi" color={black[70]}>
                日
              </Typography>
            </Stack>
          </StatCard>
        </Stack>
      </Stack>

      <CurriculumProgressBlock learnerId={learnerId} />

      <Stack mt={2} gap={1} sx={{ borderTop: `1px solid ${divider.middle}`, pt: 2 }}>
        <Typography variant="cap12" color={black[70]}>
          外国人本人宛の応援メッセージ
        </Typography>
        <Typography variant="body14Semi" color={text.primary} whiteSpace="pre-wrap">
          {encouragementMessage}
        </Typography>
      </Stack>
    </InnerBox>
  );
};
